import { apiRequest } from '@/lib/httpClient'
import { normalizePhone } from '@/lib/phone'
import { buildDIAssetUrl } from '@/api/assetsConfig'
import type {
  ClientDetail,
  ClientDetailApiResponse,
  ClientGridApiResponse,
  ClientGridApiRow,
  ClientLogoResponse,
  ClientRow,
  ClientSaveApiPayload,
  ClientSaveApiResponse,
  ClientSavePayload,
  ClientStatusId,
  ClientTypeId,
  FeatureAccessGroupRow,
  FeatureAccessPermission,
  FeatureAccessResponse,
  FeatureAccessUserTypeRow,
  PbiGroupOption,
  UpdateFeatureAccessRequest,
  UploadClientLogoPayload,
} from './types'
import { normalizeFeatureAccessPermission, normalizeFeatureAccessUserTypeName } from './types'

type RawFeatureAccessRow = {
  permission?: string
  userTypeId?: number | null
  userTypeName?: string
  enabled?: boolean
}

type RawFeatureAccessGroupRow = {
  permission?: string
  userGroupId?: string | null
  userGroupName?: string
  enabled?: boolean
}

type RawFeatureAccessPermission = {
  permission?: string
  rows?: RawFeatureAccessRow[]
  groupRows?: RawFeatureAccessGroupRow[]
}

type RawFeatureAccessFeature = {
  featureKey: string
  featureName: string
  permissions?: RawFeatureAccessPermission[]
  rows?: RawFeatureAccessRow[]
}

export type FetchClientsParams = {
  name?: string
  status?: 'Active' | 'Inactive' | 'all'
}

function asString(value: unknown) {
  if (value === null || value === undefined) return ''
  return String(value)
}

function mapClient(row: ClientGridApiRow): ClientRow {
  const clientId = row.clientId || row.ClientId || ''
  const name = row.name || row.Name || ''
  const address = row.address || row.Address || ''
  const phone = normalizePhone(row.phone || row.Phone || '')
  const email = row.email || row.Email || ''
  const contact = row.contact || row.Contact || ''
  const statusId = row.statusId ?? row.StatusId
  const typeId = row.typeId ?? row.TypeId
  const showProjectStatus = row.showProjectStatus ?? row.ShowProjectStatus
  const showAdvisoryMapping = row.showAdvisoryMapping ?? row.ShowAdvisoryMapping

  return {
    id: clientId,
    name,
    address,
    phone,
    email,
    statusId: (statusId === 2 ? 2 : 1) as ClientStatusId,
    principalContact: contact,
    typeId: ((typeId as ClientTypeId) || 1) as ClientTypeId,
    showProjectStatus: typeof showProjectStatus === 'boolean' ? showProjectStatus : undefined,
    showAdvisoryMapping: typeof showAdvisoryMapping === 'boolean' ? showAdvisoryMapping : undefined,
  }
}

function normalizeRows(payload: ClientGridApiResponse): ClientGridApiRow[] {
  if (Array.isArray(payload)) return payload
  return payload.clients || []
}

function mapClientDetail(payload: ClientDetailApiResponse): ClientDetail | null {
  const client = payload.client
  if (!client) return null

  const base = mapClient(client)
  const platformType = Array.isArray(payload.platformType)
    ? payload.platformType
    : Array.isArray(payload.PlatformType)
      ? payload.PlatformType
      : undefined

  return {
    ...base,
    powerBIWorkspace:
      asString(client.powerBIWorkspace) ||
      asString(client.PowerBIWorkspace) ||
      asString(client.powerBiWorkspace) ||
      asString(client.PowerBiWorkspace),
    powerBIWorkspaceName:
      asString(client.powerBIWorkspaceName) ||
      asString(client.PowerBIWorkspaceName) ||
      asString(client.powerBiWorkspaceName) ||
      asString(client.PowerBiWorkspaceName),
    canUseXPMUserGroup:
      typeof (client.canUseXPMUserGroup ?? client.CanUseXPMUserGroup) === 'boolean'
        ? Boolean(client.canUseXPMUserGroup ?? client.CanUseXPMUserGroup)
        : undefined,
    platformType,
    street: client.street || client.Street || '',
    suburb: client.suburb || client.Suburb || '',
    state: client.state || client.State || '',
    postcode: client.postcode || client.Postcode || '',
    primaryColor:
      (asString(client.primaryColor) || asString(client.PrimaryColor) || '').trim() || undefined,
    secondaryColor:
      (asString(client.secondaryColor) || asString(client.SecondaryColor) || '').trim() || undefined,
    textColor: (asString(client.textColor) || asString(client.TextColor) || '').trim() || undefined,
  }
}

function toApiPayload(payload: ClientSavePayload): ClientSaveApiPayload {
  return {
    ClientId: payload.clientId,
    StatusId: payload.statusId,
    TypeId: payload.typeId,
    Name: payload.name,
    Contact: payload.contact,
    Phone: payload.phone,
    Email: payload.email,
    Address: payload.address,
    Street: payload.street,
    Suburb: payload.suburb,
    State: payload.state,
    Postcode: payload.postcode,
    PowerBIWorkspace: payload.powerBIWorkspace,
    PowerBIWorkspaceName: payload.powerBIWorkspaceName,
    DatabaseName: payload.databaseName,
    ShowProjectStatus: payload.showProjectStatus,
    ShowAdvisoryMapping: payload.showAdvisoryMapping,
    Logo: payload.logo,
    IsImage: payload.isImage,
    PrimaryColor: payload.primaryColor,
    SecondaryColor: payload.secondaryColor,
    TextColor: payload.textColor,
  }
}

export async function fetchClients(params: FetchClientsParams = {}): Promise<ClientRow[]> {
  const payload = await apiRequest<ClientGridApiResponse>('Client/ui/list/grid', {
    method: 'GET',
  })

  let rows = normalizeRows(payload).map(mapClient)

  if (params.status && params.status !== 'all') {
    rows = rows.filter((r) => (params.status === 'Active' ? r.statusId === 1 : r.statusId === 2))
  }
  if (params.name?.trim()) {
    const q = params.name.trim().toLowerCase()
    rows = rows.filter((r) => r.name.toLowerCase().includes(q))
  }

  return rows
}

export async function fetchClientDetail(clientId: string): Promise<ClientDetail | null> {
  const payload = await apiRequest<ClientDetailApiResponse>('Client/ui/detail', {
    method: 'GET',
    query: { clientId },
  })

  return mapClientDetail(payload)
}

export async function createClient(payload: ClientSavePayload) {
  return apiRequest<ClientSaveApiResponse>('Client/ui/create/save', {
    method: 'POST',
    body: toApiPayload(payload),
  })
}

export async function updateClient(payload: ClientSavePayload & { clientId: string }) {
  return apiRequest('Client/ui/edit/save', {
    method: 'POST',
    body: toApiPayload(payload),
  })
}

export async function deleteClient(clientId: string): Promise<void> {
  await apiRequest<void>(`Client/ui/edit/delete/${clientId}`, {
    method: 'POST',
    body: {},
  })
}

export async function fetchPbiGroups() {
  const payload = await apiRequest<PbiGroupOption[]>('Client/ui/pbi/groups', {
    method: 'GET',
  })

  if (!Array.isArray(payload)) return []
  return payload.map((group) => ({
    value: asString(group.value),
    name: asString(group.name),
  }))
}

export async function uploadClientLogo(payload: UploadClientLogoPayload) {
  return apiRequest<boolean>('Client/ui/client/upload/logo', {
    method: 'POST',
    body: payload,
  })
}

export async function fetchClientLogo(clientId: string) {
  const payload = await apiRequest<ClientLogoResponse>('Client/ui/client/logo', {
    method: 'GET',
    query: { clientId },
  })

  const key = payload?.key || ''
  return {
    key,
    url: buildDIAssetUrl(key, String(Date.now())),
  }
}

export async function deleteClientLogo(clientId: string) {
  return apiRequest<boolean>('Client/ui/client/logo', {
    method: 'DELETE',
    query: { clientId },
  })
}

export async function fetchClientFeatureAccess(clientId: string) {
  const payload = await apiRequest<FeatureAccessResponse>(`FeatureAccess/client/${clientId}`, {
    method: 'GET',
  })

  return normalizeFeatureAccessResponse(payload)
}

export async function updateClientFeatureAccess(clientId: string, payload: UpdateFeatureAccessRequest) {
  const response = await apiRequest<FeatureAccessResponse>(`FeatureAccess/client/${clientId}`, {
    method: 'PUT',
    body: payload,
  })

  return normalizeFeatureAccessResponse(response)
}

function normalizeFeatureAccessResponse(payload: FeatureAccessResponse): FeatureAccessResponse {
  return {
    ...payload,
    features: ((payload.features || []) as RawFeatureAccessFeature[]).map((feature) => {
      const featureRows = Array.isArray(feature.rows) ? feature.rows : []
      const rawPermissions = Array.isArray(feature.permissions)
        ? feature.permissions
        : Array.from(
            featureRows.reduce<Map<string, RawFeatureAccessPermission>>((acc, row) => {
              const permission = normalizeFeatureAccessPermission(asString(row.permission))
              if (!permission) return acc
              const existing = acc.get(permission)
              if (existing) {
                existing.rows = [...(existing.rows || []), row]
              } else {
                acc.set(permission, { permission, rows: [row], groupRows: [] })
              }
              return acc
            }, new Map()).values()
          )

      return {
        ...feature,
        permissions: Array.from(
          rawPermissions
            .map<FeatureAccessPermission>((permission) => ({
              permission: normalizeFeatureAccessPermission(asString(permission.permission)),
              rows: (permission.rows || []).map<FeatureAccessUserTypeRow>((row) => ({
                permission: normalizeFeatureAccessPermission(asString(row.permission)),
                userTypeId: row.userTypeId ?? null,
                userTypeName: normalizeFeatureAccessUserTypeName(asString(row.userTypeName)),
                enabled: Boolean(row.enabled),
              })),
              groupRows: (permission.groupRows || []).map<FeatureAccessGroupRow>((row) => ({
                permission: normalizeFeatureAccessPermission(asString(row.permission)),
                userGroupId: row.userGroupId ?? null,
                userGroupName: asString(row.userGroupName),
                enabled: Boolean(row.enabled),
              })),
            }))
            .reduce<Map<string, FeatureAccessPermission>>(
              (acc, permission) => {
                const existing = acc.get(permission.permission)
                if (existing) {
                  existing.rows.push(...permission.rows)
                  existing.groupRows.push(...permission.groupRows)
                } else {
                  acc.set(permission.permission, {
                    permission: permission.permission,
                    rows: [...permission.rows],
                    groupRows: [...permission.groupRows],
                  })
                }
                return acc
              },
              new Map()
            )
            .values()
        ),
      }
    }),
    metadata: payload.metadata
      ? {
          ...payload.metadata,
          editablePermissions: Array.from(new Set(payload.metadata.editablePermissions?.map(normalizeFeatureAccessPermission) || [])),
          groups: (payload.metadata.groups || [])
            .map((group) => ({
              groupId: asString(group.groupId),
              name: asString(group.name),
            }))
            .filter((group) => group.groupId && group.name),
        }
      : undefined,
  }
}
